import { useCopilotTool } from '@customerio/hydra-copilot/react';
import { cache, type InvalidateCacheEntry } from '@customerio/hydra-nav-state';
import {
  analyzeBashCommand,
  getMutatingResources,
  DYNAMIC_PLACEHOLDER,
  type CurlResource,
} from '../approval/parse-curl-resources';

// Build an invalidation entry from a parsed curl resource. Optional fields are
// only included when present so consumers don't see explicit `undefined`s.
//  - id: omitted for creates (POST /collection) — the new id is in the
//    response body, not the URL. Consumers treat a missing id as "the whole
//    collection of this type changed."
//  - parentType / parentId: set only for nested URLs like
//    `.../campaigns/123/notes`, so consumers can scope the refresh to the
//    relevant parent (e.g. "only when viewing campaign 123").
function entryFromResource(resource: CurlResource): InvalidateCacheEntry {
  const entry: InvalidateCacheEntry = { type: resource.resourceType };
  if (resource.resourceId != null) {
    entry.id = resource.resourceId;
  }
  if (resource.parentType != null && resource.parentId != null) {
    entry.parentType = resource.parentType;
    entry.parentId = resource.parentId;
  }
  return entry;
}

export async function invalidateCacheForCommand(command: string) {
  const analysis = await analyzeBashCommand(command);
  const mutating = getMutatingResources(analysis.resources);

  if (mutating.length === 0) {
    return;
  }

  // We deliberately do NOT gate on `resource.fullyResolved`. That flag is true
  // only when the *entire* URL is statically resolved, so a dynamic segment
  // anywhere — including the `environments/$ENV_ID` namespace prefix that
  // extractResourceType/extractParent strip out — would suppress an otherwise
  // perfectly-formed invalidation (e.g. `.../environments/$ENV_ID/campaigns/4`
  // resolves type `campaigns` and id `4`, yet fullyResolved is false).
  //
  // Invalidation only needs the resource type. The id/parentId fields already
  // self-degrade: a dynamic terminal id leaves `resourceId` undefined, which
  // entryFromResource turns into a collection-wide invalidate ("this whole type
  // changed"). The only thing we can't emit is an entry whose own type is
  // dynamic (e.g. `.../$RESOURCE/123`), so we filter those out.
  const entries = mutating
    .filter((resource) => !resource.resourceType.includes(DYNAMIC_PLACEHOLDER))
    .map(entryFromResource);

  if (entries.length > 0) {
    cache.invalidate(entries);
  }
}

export function BashTool() {
  useCopilotTool({
    type: 'server',
    name: 'bash',
    handler({ args, response, wasApproved }) {
      if (response?.error != null) {
        return;
      }

      if (wasApproved !== true) {
        return;
      }

      void invalidateCacheForCommand(String(args.command));
    },
  });

  return null;
}
